import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import InfoIcon from '@material-ui/icons/Info';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {Card, CardContent, Typography, CardActions, CardHeader, IconButton, Tooltip} from "@material-ui/core";
import TankImage from "./assets/img/barrel-empty-large.png";
import {TANK_STATUSES} from "../../data";
import useFormatDate from "../hooks/useFormatDate";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  'tank-card': {
    display: 'flex',
    flexDirection: 'column',
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: 'white',
    borderRadius: '2px'
  },
  cardHeaderRoot: {
    paddingBottom: 0
  },
  cardHeader: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardActionsRoot: {
    paddingTop: 0
  },
  title: {
    fontSize: 14,
  },
  pos: {
    marginBottom: 12,
  },
  'tank-gauge': {
    width: '100px',
    height: '140px',
    position: 'relative',
    margin: '5px auto',
  },
  'empty-barrel': {
    position: 'absolute',
    height: '100%',
    width: '100%',
    backgroundImage: `url(${TankImage})`,
    background: 'no-repeat center',
    backgroundSize: '100px 140px',
    zIndex: 4,
  },
  fluid: {
    position: 'absolute',
    width: '92px',
    height: '127px',
    margin: '5px 4px 8px',
    zIndex: 1
  },
  'tank-volume': {
    position: 'relative',
    transform: 'translateY(95%)',
    textAlign: 'center',
    zIndex: 4
  },
  level: {
    position: 'absolute',
    width: '100%',
    maxHeight: '100%',
    borderRadius: '2px',
    bottom: 0,
    transition: 'height 2s ease-out'
  },
  bottom: {
    position: 'absolute',
    width: '100%',
    height: '12px',
    borderRadius: '240px / 30px',
    bottom: '-4px',
    zIndex: 1
  },
  footer: {
    justifyContent: 'center'
  }
});

const TankCard = ({tank, displayUnits, baseUrl, isEditor}) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const formattedDate = useFormatDate(tank.ReadingTime_UTC);
  let bgColors = {
    OK: '#319B42',
    LOW_LEVEL: '#E87722',
    CRITICALLY_LOW_LEVEL: '#E4002B',
    ERROR: '#A6093D',
  };
  const tankCapacity = tank.volume_disp;
  const fullnessPercentage = tank.volume_pct
  let currentColor;
  var tankStatus = "OK";
  if ((typeof tank.Status) !== "string") {
    tankStatus = "";
  }
  else if (tank.Status.includes("Comm")) {
    tankStatus = "Comm";
  }
  else if (tank.Status.includes("SensErr")){
    tankStatus = "SensErr";
  }
  else if (tank.Status.includes("Crit")) {
    tankStatus = "Critical";
  }
  else if (tank.Status.includes("Batt")) {
    tankStatus = "Batt";
  }
  else if (tank.Status.includes("High")) {
    tankStatus = "High";
  }
  else if (tank.Status.includes("Low")) {
    tankStatus = "Low";
  }
  else if (tank.Status.includes("SensW")){
    tankStatus = "SensW";
  }
  else if (tank.Status.includes("None")) {
    tankStatus = "None";
  }
  
  switch (tankStatus) {
    case TANK_STATUSES.LOW:
      currentColor = bgColors.LOW_LEVEL;
      break;
    case TANK_STATUSES.SENSOR_ERROR:
      currentColor = bgColors.ERROR;
      break;
    case TANK_STATUSES.SENSOR_WARNING:
      currentColor = bgColors.LOW_LEVEL;
      break;
    case TANK_STATUSES.CRITICALLY_LOW:
      currentColor = bgColors.CRITICALLY_LOW_LEVEL;
      break;
    case TANK_STATUSES.COMMUNICATION_ERROR:
      currentColor = bgColors.ERROR;
      break;
    case TANK_STATUSES.OK:
      currentColor = bgColors.OK;
      break;
    default:
      currentColor = bgColors.ERROR;
  }

  let levelHeight;

  if (fullnessPercentage > 100) {
    levelHeight = '100%';
  } else if (fullnessPercentage < 0) {
    levelHeight = '0%';
  } else {
    levelHeight = fullnessPercentage + '%';
  }
  const formatStatus = (statusIn) => {
    var statusOut = "";
    var statusTemp = statusIn.toLowerCase().split(",");
    for (var i=0; i<statusTemp.length; i++){
      statusTemp[i] = t(statusTemp[i]);
    }
    statusOut = statusTemp.join(", ");
    //console.log(statusIn);
    //console.log(statusOut);
    return statusOut;
  }

  return (
    <Card className={classes['tank-card']}>
      <CardHeader
        classes={{
          root: classes.cardHeaderRoot,
          content: classes.cardHeader,
        }}
        title={
          <Typography noWrap gutterBottom variant="h5" component="span">
            {tank.name}
          </Typography>
        }
        subheader={
          <Typography noWrap variant="subtitle1">
            {tank.fluidName}
          </Typography>
          }
      />
      <CardActions disableSpacing
                   classes={{
                     root: classes.cardActionsRoot,
                   }}>
        {isEditor && <Tooltip title={t("edit")} placement="top">
          <IconButton aria-label={t("edit")}
                      component={RouterLink}
                      to={baseUrl+'/edit'}>
            <EditIcon/>
          </IconButton>
        </Tooltip>}
        <Tooltip title={t("info")} placement="top">
          <IconButton aria-label={t("info")}
                      component={RouterLink}
                      to={baseUrl}>
            <InfoIcon/>
          </IconButton>
        </Tooltip>
      </CardActions>
      <CardContent>
        <div className={classes['tank-gauge']}>
          <div className={classes['empty-barrel']} />
          <div className={classes.fluid}>
            <div className={classes['tank-volume']}>
              {tank.status !== TANK_STATUSES.COMMUNICATION_ERROR
                ? <Typography variant="body2" component="div">
                  <div className="text-truncate">{tankCapacity}</div>
                </Typography>
                : <ErrorOutlineIcon/>
              }
              <Typography variant="body2" component="div">
                <div className="text-truncate">{displayUnits.includes(t("metric")) ? t("liters") : t("gallons")}</div>
              </Typography>
            </div>
            <div className={classes.level} style={{'backgroundColor': currentColor, 'height': levelHeight}}/>
            <div className={classes.bottom} style={{'backgroundColor': currentColor}}/>
          </div>
        </div>
      </CardContent>
      <CardActions className={classes.footer}>
        <div className="text-center">
          <Typography variant="body2" component="p">
            {fullnessPercentage}% - {tankCapacity} {displayUnits.includes(t("metric")) ? t("liters") : t("gallons")}
          </Typography>
          <Typography variant="body2" component="p">
            {formattedDate}
          </Typography>
          <Typography variant="body2" component="p" style={{fontWeight: 600}} >
            {tank.Status === t("ok") ? "" : formatStatus(tank.Status)}
          </Typography>
        </div>
      </CardActions>
    </Card>)
};

export default TankCard;
